import { batch } from 'react-redux';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { AbortedError } from '../common/api/extended-emerald-error';
import { updateDeviceStatus } from '../common/device/initialize-devices';
import { popEmeraldModal } from '../common/emerald-modal-manager/emerald-modal-manager';
import { DeviceType } from '../common/enumerations/device-type';
import { getStableWeight, getStatusCodeId } from '../common/helpers/get-stable-weight';
import XLRLogger from '../common/helpers/logger';
import { store } from '../common/redux/configure-store';
import { ScaleActions } from '../common/redux/reducers/scale-reducer';
import { RdsResponseType, ScaleStatus } from './device-enums';
import { DeviceResponse } from './rds-connection';
import { readWeight } from './helper/read-weight';
import { scaleErrorMessage, ScaleErrorMessageProps } from './scale-error-message';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';

const loggerSource = 'devices/scale-operations.ts';

export const handleScaleMessage = (scaleData: DeviceResponse): void => {
  if (scaleData.error) {
    XLRLogger.error({
      message: 'Handle Scale Message Error',
      source: loggerSource,
      data: scaleData,
    });
  } else {
    handleResponseType(scaleData);
  }
};

const handleResponseType = (scaleData: DeviceResponse): void => {
  const deviceName = scaleData.metaDataMap.logicalName;
  const status = scaleData.metaDataMap.status ?? 'NotSet';
  const scaleStatus = status as ScaleStatus;
  const { weight } = scaleData.metaDataMap;
  const { responseType } = scaleData;
  const currentScaleDeviceStatus = store
    .getState()
    .deviceStatus.devices.find(device => device.deviceType === DeviceType.Scale);

  if (responseType === RdsResponseType.STATUS_UPDATE) {
    XLRLogger.info({
      message: `Scale Change Status: ${scaleStatus}, weight: ${weight}`,
      source: loggerSource,
      data: scaleData,
    });
    batch(() => {
      store.dispatch(ScaleActions.setScaleStatus(scaleStatus));
      store.dispatch(ScaleActions.setScaleWeight(+(weight ?? '')));
      if (scaleStatus === ScaleStatus.NOT_READY) {
        updateDeviceStatus({
          deviceName: currentScaleDeviceStatus?.logicalName || DeviceType.Scale,
          initialized: false,
          deviceType: DeviceType.Scale,
        });
      } else if (scaleStatus !== ScaleStatus.UNKNOWN_STATUS) {
        updateDeviceStatus({
          deviceName: currentScaleDeviceStatus?.logicalName || DeviceType.Scale,
          initialized: true,
          deviceType: DeviceType.Scale,
        });
      }
    });
  } else if (responseType === RdsResponseType.HEALTH_CHECK_SUCCESS) {
    // for scale, if NOT_READY is the status, do not set to initialized.
    if (deviceName) {
      updateDeviceStatus({
        deviceName,
        initialized: store.getState().scale.status !== ScaleStatus.NOT_READY,
        deviceType: DeviceType.Scale,
      });
    }
    XLRLogger.debug({
      message: `Scale Status: ${scaleData.message}`,
      source: loggerSource,
      data: scaleData,
    });
  } else if (responseType === RdsResponseType.HEALTH_CHECK_FAILURE) {
    if (deviceName) {
      updateDeviceStatus({
        deviceName,
        initialized: false,
        deviceType: DeviceType.Scale,
      });
    }
    XLRLogger.debug({
      message: `Scale Status: ${scaleData.message}`,
      source: loggerSource,
      data: scaleData,
    });
  } else {
    XLRLogger.info({
      message: 'Unrecognized message from Scale',
      source: loggerSource,
      data: scaleData,
    });
  }
};

export const getWeightIfStable = async (
  itemId: string = '',
  itemDescription: string = '',
): Promise<number> => {
  const span = createSpan('get-stable-weight');
  let defaultWeight = getStableWeight();
  let communicationError = false;
  if (defaultWeight) {
    endSpan(span);
    return defaultWeight;
  } else {
    let { errorTitleId, errorMessageId } = getStatusCodeId();

    //if not connected to scale, don't even try to read weight, just show connection error
    const isConnected = store
      .getState()
      .deviceStatus.devices.find(d => d.deviceType === DeviceType.Scale && d.initialized);

    if (!isConnected) {
      communicationError = true;
      errorMessageId = 'scale_communication_instructional_text';
      errorTitleId = 'scale_communication_error';
    }
    // TODO: promise executor function should not be async
    // eslint-disable-next-line
    await new Promise<void>(async (resolve, reject) => {
      let cancelPressed = false;
      const scaleErrorMessageProps: ScaleErrorMessageProps = {
        retry: () => {
          defaultWeight = getStableWeight();
          if (defaultWeight) {
            resolve();
            popEmeraldModal();
          }
        },
        cancel: () => {
          popEmeraldModal();
          cancelPressed = true;
          reject(new AbortedError());
        },
        errorTitleId,
        itemId,
        itemDescription,
        errorMessageId,
      };
      scaleErrorMessage(scaleErrorMessageProps);
      while (!defaultWeight && !cancelPressed && !communicationError) {
        const response = await readWeight(3000);
        if (response.success) {
          defaultWeight = getValueAsNumber(response.message);

          if (defaultWeight) {
            popEmeraldModal();
            resolve();
          }
        }
      }
    }).finally(() => endSpan(span));
  }
  return defaultWeight;
};
